import { createStore } from "solid-js/store";
import { A, createAsync, useNavigate, query } from "@solidjs/router";
import { createSignal, createMemo, For, Show, Suspense, createEffect, Switch, Match } from "solid-js";

import { PAGE_SIZE } from "~/_server/constants";

import { Certificate } from "~/components";
import { CertificateButtons } from "./CertificateButtons";
import { formattedTimestamp } from "~/lib";
import type { Project } from "~/lib";
import { getByUserQuery } from "~/lib";
import { useProfile } from "~/context/ProfileProvider";

const localByUser = query( async ( rowType: string, limit = 25, skId = null ) => {
	"use server";

	const { getByUser } = await import( "~/_server/dynamoDB" );

	return await getByUser( rowType, null, limit, skId );
}, "localBYUser" );

export const ProjectsTable = ( props ) => {
	const { profile } = useProfile();

	const [projectStore, setProjectStore] = createStore( [] );

	const allProjects = createAsync( () => localByUser( "PROJECT", -1 ) );
	const allCertificates = createAsync( () => localByUser( "CERTIFICATE", -1 ) );

	createEffect( () => {
		if ( allProjects()?.items ) {
			setProjectStore( () => allProjects()?.items );
		}
	} );

	const latestCerts = createMemo( () => {
		const out = {};

		if ( allCertificates()?.items ) {
			for ( const cert of allCertificates()?.items ) {
				const id = cert.parent;
				if ( !out[id] || cert.created > out[id].created ) {
					out[id] = cert;
				}
			}
		}

		return out;
	} );

	const navigate = useNavigate();
	const [page, setPage] = createSignal( 0 );
	const [search, setSearch] = createSignal( "" );

	const pagedProjects = createMemo( () => {
		const filtered = search()
			? projectStore.filter(
				( p ) =>
					p.name.toLowerCase().includes( search().toLowerCase() ) ||
					p.description.toLowerCase().includes( search().toLowerCase() ),
			)
			: projectStore;
		const start = page() * PAGE_SIZE;

		return filtered
			?.map( ( f ) => ( { ...f, id: f.sk.split( "#" )[1] } ) )
			.slice( start, start + PAGE_SIZE );
	} );

	return (
		<Switch>
			<Match when={profile()?.role !== "certifier"}>
				<h2>You need to be an active certifier in order to add projects.</h2>
			</Match>

			<Match when={profile()?.role === "certifier"}>
				<Show when={!props.slimView}>
					<div class="flex flex-nowrap justify-between mb-10">
						<input
							value={search()}
							type="text"
							class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-1 focus:-outline-offset-1 focus:outline-primary sm:text-sm/6"
							placeholder="Search projects"
							onInput={( e ) => setSearch( e.currentTarget.value )}
						/>

						<A
							href="/dashboard/projects/new"
							class="ml-4 text-nowrap hover:bg-blue-700  font-bold py-2 px-4 rounded bg-primary text-white">
							Create New Project
						</A>
					</div>
				</Show>


				<div
					class="flex flex-col md:grid italic py-3 border border-x-0 border-gray-500"
					classList={{
						"md:grid-cols-9": props.slimView,
						"md:grid-cols-11": !props.slimView,
					}}>
					<Show when={!props.slimView}>
						<span>Created</span>
					</Show>
					<span class="col-span-4">Name</span>
					<Show when={!props.slimView}>
						<span>Local Authority</span>
					</Show>
					<span class="text-center">Plots</span>
					<span class="col-span-2">Certificates</span>
					<span class="col-span-2">&nbsp;</span>
				</div>

				<Suspense fallback={<div>Loading...</div>}>
					<For each={pagedProjects()}>
						{( item: Partial<Project> ) => {
							const cert = latestCerts()[item.sk];

							return (
								<div
									class="flex flex-wrap py-4 md:p-0 border-b border-gray-300 md:border-0 md:flex-col md:grid md:odd:bg-white md:even:bg-slate-50 items-center w-full"
									classList={{
										"md:grid-cols-9": props.slimView,
										"md:grid-cols-11": !props.slimView,
									}}>
									<Show when={!props.slimView}>
										<span class="w-full block text-nowrap row-span-2 pl-2">
											{item.created && formattedTimestamp( item.created )}
										</span>
									</Show>

									<span
										onClick={() => {
											navigate( `/dashboard/projects/${item.id}`, {
												state: { projectId: item.id },
											} );
										}}
										class="w-2/3 md:w-full block cursor-pointer font-bold text-nowrap col-span-4 overflow-hidden text-ellipsis"
										classList={{ "md:pl-2": props.slimView }}>
										{item.name}
									</span>

									<Show when={!props.slimView}>
										<span
											class="w-full block text-nowrap"
											innerHTML={item.localAuthority}
										/>
									</Show>

									<span class="w-1/3 md:w-full block text-nowrap text-center">
										<span class="md:hidden">Plots: </span>
										{item.plots?.length}
									</span>

									<span class="pt-2 md:p-0 w-full flex flex-col col-span-2">
										<Suspense fallback={<span>...</span>}>
											<Certificate certificate={cert} style="badge" />
										</Suspense>
									</span>

									<span class="w-full flex flex-nowrap col-span-2">
										<Suspense fallback={<span>...</span>}>
											<CertificateButtons
												certificate={cert}
												projectId={item.id}
											/>
										</Suspense>
									</span>

									<Show when={!props.slimView}>
										<span class="text-xs col-start-2 col-end-9 my-2">
											{item.description}
										</span>
									</Show>
								</div>
							);
						}}
					</For>
				</Suspense>

				<Show when={!props.noNav}>
					<nav
						aria-label="Pagination"
						class="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
						<div class="hidden sm:block">
							<p class="text-sm text-gray-700">
								Showing
								<span class="font-medium">&nbsp;{page() * PAGE_SIZE + 1}&nbsp;</span>
								to
								<span class="font-medium">
									&nbsp;{page() * PAGE_SIZE + PAGE_SIZE}&nbsp;
								</span>
								of
								<span class="font-medium">&nbsp;{projectStore.length}&nbsp;</span>
								projects
							</p>
						</div>
						<div class="flex flex-1 justify-between sm:justify-end">
							<button
								type="button"
								disabled={page() === 0}
								onClick={() => setPage( ( p ) => p - 1 )}
								class="rias disabled:bg-gray-50 disabled:text-gray-500 cursor-pointer relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 inset-ring inset-ring-gray-300 hover:bg-gray-50">
								Previous
							</button>
							<button
								type="button"
								disabled={PAGE_SIZE * page() + 1 >= projectStore.length}
								onClick={() => setPage( ( p ) => p + 1 )}
								class="rias disabled:bg-gray-50 disabled:text-gray-500 cursor-pointer relative ml-3 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 inset-ring inset-ring-gray-300 hover:bg-gray-50">
								Next
							</button>
						</div>
					</nav>
				</Show>
			</Match>
		</Switch>
	);
};
